/**
 * Какие коды причин аномалии показываются пользователю — и ТОЛЬКО это.
 *
 * ЗАЧЕМ. Словарь причин содержит две историчные проверки по одной и той же
 * единице, которые в тексте читаются как один и тот же факт:
 *
 *   NEW_UNIT_TRANSITION   — «Новая комбинация для вагона»: пара кодов груза
 *                           «ввоз → вывоз» для этого вагона/контейнера ранее
 *                           не встречалась.
 *   NEW_UNIT_EXPORT_CARGO — «Новый код груза на вывоз для вагона»: код груза
 *                           на вывозе для этого вагона/контейнера ранее не
 *                           встречался.
 *
 * Аналитически это РАЗНЫЕ признаки: первый смотрит на пару кодов, второй — на
 * один код экспортной ноги. Но код вывоза входит в саму пару, поэтому когда
 * сработали оба, вторая карточка ничего не добавляет к первой и читается как её
 * повтор другими словами. В текущем активном снимке это не редкий случай:
 * NEW_UNIT_EXPORT_CARGO встречается 7 323 раза и ВСЕ 7 323 раза вместе с
 * NEW_UNIT_TRANSITION (см.
 * ai_transit/v2/reports/ANOMALY_REASON_UI_DEDUP_NEW_UNIT_EXPORT_CARGO.md).
 *
 * ЧТО ЭТО НЕ ДЕЛАЕТ. Здесь нет аналитики. Модуль не трогает балл, уровень,
 * когорту, доказательства и сам массив `anomalyReasons`: он принимает коды и
 * возвращает НОВЫЙ массив кодов для показа. Аналитические счётчики причин
 * по-прежнему считают NEW_UNIT_EXPORT_CARGO во всех 7 323 кейсах — расхождение
 * с числом видимых карточек ожидаемо и «чинить» его не нужно.
 *
 * ГРАНИЦЫ ПРИМЕНЕНИЯ. Оба кода принадлежат словарю сценариев 1–5
 * (`anomaly_reason_dictionary_ru_v1`). У сценариев 6–7 свой непересекающийся
 * словарь из восьми кодов `UNUSUAL_*` (`cross-unit.i18n.ts`), и ни одного
 * вхождения этих двух кодов там нет — проверено на 105 557 строках выдачи
 * сценариев 6 и 7. Поэтому cross-unit-поверхность правило не затрагивает.
 */

/** Пара «общее → частное»: правый код скрывается, когда присутствует левый. */
export const SUBSUMED_ANOMALY_REASON = 'NEW_UNIT_EXPORT_CARGO'
export const SUBSUMING_ANOMALY_REASON = 'NEW_UNIT_TRANSITION'

/**
 * Коды причин, которые показываются пользователю.
 *
 * Исходный массив не изменяется: возвращается новый. Порядок уцелевших кодов —
 * ровно тот, в котором они пришли; ничего не добавляется и не переставляется.
 *
 * @param reasonCodes ПОЛНЫЙ аналитический набор кодов кейса (`anomalyReasons`
 *   строки реестра либо объединение поддержанных и неподдержанных кодов записи
 *   объяснения) — решение принимается по нему, а не по тому подмножеству,
 *   которое уже дошло до карточек.
 */
export function getVisibleAnomalyReasons(reasonCodes: readonly string[] | null | undefined): string[] {
  const codes = reasonCodes ?? []
  const hasBoth =
    codes.includes(SUBSUMING_ANOMALY_REASON) && codes.includes(SUBSUMED_ANOMALY_REASON)
  return hasBoth ? codes.filter((c) => c !== SUBSUMED_ANOMALY_REASON) : [...codes]
}

/**
 * Тот же ответ множеством — для мест, где надо отфильтровать не коды, а объекты
 * причин (карточки, доказательства), сохранив их собственный порядок.
 */
export function visibleAnomalyReasonSet(reasonCodes: readonly string[] | null | undefined): Set<string> {
  return new Set(getVisibleAnomalyReasons(reasonCodes))
}
